// import components
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { DatePicker } from 'components/shared';
import { Box, Typography } from '@mui/material';

// import hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import styles
import {
  StyledBoxFlexAlignCenterSpaceBetween,
  StyledBoxFlexColumn,
  StyledBoxFlexColumnLargeGap,
  StyledBoxFlexColumnSmallGap
} from '../Box/Box.styles';

// import types
import {
  PeriodTypeEnum,
  RecurrenceRuleProps,
  RecurrenceRuleType
} from '../PlanningCycle/PlanningCycle.types';

// import utils
import { WeekDays } from 'utils/date';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import {
  getDefaultStartString,
  getNegativeWeekOfMonth,
  getWeekOfMonth
} from './RecurrenceRule.util';

export default function RecurrenceRule({
  onRecurrenceRuleChange,
  recurrenceRule
}: RecurrenceRuleProps) {
  const { t } = useTranslation('common');

  const [start, setStart] = useState<string>(
    recurrenceRule?.start || getDefaultStartString()
  );

  const [type, setType] = useState<PeriodTypeEnum>(
    recurrenceRule?.periodType || PeriodTypeEnum.MONTH
  );

  const defaultWeekDays = recurrenceRule?.dayInWeekSet || [
    dayjs(start).locale('en').format('dddd').toUpperCase()
  ];

  const [selectedWeekDays, setSelectedWeekDays] =
    useState<string[]>(defaultWeekDays);
  const [interval, setInterval] = useState<number>(
    recurrenceRule?.interval || 1
  );

  function getDefaultMonthOption(type?: RecurrenceRuleType | null) {
    if (type) {
      if (type.dayOfMonth) return 'dayOfMonth';
      if (type.setPosition && type.setPosition < 0 && start) {
        return getNegativeWeekOfMonth(start) >= -2
          ? 'setPositionNegative'
          : 'setPosition';
      }
      return 'setPosition';
    }
    return getWeekOfMonth(getDefaultStartString()) < 5
      ? 'setPosition'
      : 'setPositionNegative';
  }

  function getDefaultDayOfMonth(type?: RecurrenceRuleType | null) {
    if (type && getDefaultMonthOption(type) === 'dayOfMonth') {
      return new Date(type.start).getDate();
    }
    return undefined;
  }

  function getDefaultSetPosition(type?: RecurrenceRuleType | null) {
    const fallbackStart = type ? type.start : getDefaultStartString();
    const monthOption = getDefaultMonthOption(type);
    if (monthOption === 'setPosition') {
      const w = getWeekOfMonth(fallbackStart);
      return w < 5 ? w : getNegativeWeekOfMonth(fallbackStart);
    }
    if (monthOption === 'setPositionNegative') {
      const negW = getNegativeWeekOfMonth(fallbackStart);
      return negW > -3 ? negW : getWeekOfMonth(fallbackStart);
    }
    return undefined;
  }

  const [selectedMonthOption, setSelectedMonthOption] = useState<string>(
    getDefaultMonthOption(recurrenceRule)
  );
  const [dayOfMonth, setDayOfMonth] = useState<number | undefined>(
    getDefaultDayOfMonth(recurrenceRule)
  );
  const [setPosition, setSetPosition] = useState<number | undefined>(
    getDefaultSetPosition(recurrenceRule)
  );

  useEffect(() => {
    if (!start) {
      onRecurrenceRuleChange(null);
      return;
    }

    const newRule: RecurrenceRuleType = {
      start,
      periodType: type,
      interval
    };

    if (type === PeriodTypeEnum.WEEK) {
      newRule.dayInWeekSet = selectedWeekDays;
    }

    if (type === PeriodTypeEnum.MONTH) {
      newRule.dayOfMonth = dayOfMonth;
      newRule.setPosition = setPosition;
      if (setPosition !== undefined) {
        newRule.dayInWeekSet = [WeekDays[dayjs(start).day()]];
      }
    }

    onRecurrenceRuleChange(newRule);
  }, [
    start,
    type,
    interval,
    selectedWeekDays,
    dayOfMonth,
    setPosition,
    onRecurrenceRuleChange
  ]);

  const onStartDateChanged = (pickerDate: Date | null) => {
    if (pickerDate) {
      const isoString = pickerDate.toISOString();

      if (type === PeriodTypeEnum.MONTH) {
        switch (selectedMonthOption) {
          case 'dayOfMonth':
            setDayOfMonth(pickerDate.getDate());
            break;
          case 'setPosition': {
            const w = getWeekOfMonth(isoString);
            if (w < 5) {
              setSetPosition(w);
            } else {
              setSetPosition(getNegativeWeekOfMonth(isoString));
              setSelectedMonthOption('setPositionNegative');
            }
            break;
          }
          case 'setPositionNegative': {
            const neg = getNegativeWeekOfMonth(isoString);
            if (neg > -3) {
              setSetPosition(neg);
            } else {
              setSetPosition(getWeekOfMonth(isoString));
              setSelectedMonthOption('setPosition');
            }
            break;
          }
        }
      } else {
        setSelectedWeekDays([WeekDays[pickerDate.getDay()]]);
        setDayOfMonth(undefined);
        setSetPosition(undefined);
      }

      setStart(isoString);
    } else {
      setStart('');
    }
  };

  const onSetDayOfMonth = (day: number) => {
    setSetPosition(undefined);
    setDayOfMonth(day);
    setSelectedMonthOption('dayOfMonth');
  };

  const onSetPositionClick = (pos: number) => {
    setDayOfMonth(undefined);
    setSetPosition(pos);
    setSelectedMonthOption(pos > 0 ? 'setPosition' : 'setPositionNegative');
  };

  const weekOfMonthVal = getWeekOfMonth(start);
  const negativeWeekOfMonthVal = getNegativeWeekOfMonth(start);
  const numericDay = Number(dayjs(start).format('D'));
  const dayName = dayjs(start).format('dddd');

  return (
    <StyledBoxFlexColumnLargeGap>
      <Box>
        <Typography variant="h6">{t('startDate')}</Typography>
        <DatePicker
          date={dayjs(start).toDate()}
          onChange={onStartDateChanged}
          sx={{
            maxWidth: '240px'
          }}
        />
      </Box>
      <Box>
        <Typography variant="h6">{t('recurrenceRule')}</Typography>
        <StyledBoxFlexColumnSmallGap
          sx={{
            maxWidth: '240px'
          }}
        >
          <StyledBoxFlexColumn>
            <StyledBoxFlexAlignCenterSpaceBetween>
              <Typography
                variant="body1"
                sx={{
                  minWidth: 'max-content'
                }}
              >
                {t('repeatEvery')}
              </Typography>
              <TextField
                value={interval}
                type="number"
                variant="outlined"
                className="inline-input"
                size="small"
                fullWidth
                onChange={(e) => setInterval(parseInt(e.target.value, 10))}
                inputProps={{ min: 1 }}
              />
              <Select
                value={type}
                variant="outlined"
                size="small"
                onChange={(e) => setType(e.target.value as PeriodTypeEnum)}
              >
                <MenuItem value={PeriodTypeEnum.WEEK}>
                  {t(`week${interval === 1 ? '' : 's'}`)}
                </MenuItem>
                <MenuItem value={PeriodTypeEnum.MONTH}>
                  {t(`month${interval === 1 ? '' : 's'}`)}
                </MenuItem>
              </Select>
            </StyledBoxFlexAlignCenterSpaceBetween>
            {type === PeriodTypeEnum.WEEK && (
              <Box>
                <Typography variant="body1">
                  {t('repeatOn')}{' '}
                  {selectedWeekDays.length > 0 && (
                    <Typography variant="body2" display="inline">
                      {t(`planningCycle.${selectedWeekDays[0].toLowerCase()}`)}
                    </Typography>
                  )}
                </Typography>
              </Box>
            )}
          </StyledBoxFlexColumn>

          {type === PeriodTypeEnum.MONTH && start && (
            <>
              <Select
                variant="outlined"
                size="small"
                value={selectedMonthOption}
              >
                {weekOfMonthVal < 5 && (
                  <MenuItem
                    value="setPosition"
                    onClick={() => onSetPositionClick(weekOfMonthVal)}
                  >
                    {t(`onThe${weekOfMonthVal}`)} {dayName}
                  </MenuItem>
                )}
                {negativeWeekOfMonthVal > -3 && (
                  <MenuItem
                    value="setPositionNegative"
                    onClick={() => onSetPositionClick(negativeWeekOfMonthVal)}
                  >
                    {t(`onThe${negativeWeekOfMonthVal}`)} {dayName}
                  </MenuItem>
                )}
                <MenuItem
                  value="dayOfMonth"
                  onClick={() => onSetDayOfMonth(numericDay)}
                >
                  {t('onDay')} {numericDay}
                </MenuItem>
              </Select>
            </>
          )}
        </StyledBoxFlexColumnSmallGap>
      </Box>
    </StyledBoxFlexColumnLargeGap>
  );
}
